var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import { Animated, Dimensions, Platform, StyleSheet } from "react-native";
import { Box, FlatList, Popover, Pressable, Stack, Text, useTheme, } from "native-base";
import { isWeb } from "@madmedical/utils";
import { useTranslation } from "@madmedical/i18n";
import Button from "../atoms/Button";
import Icon from "../icons/Icon";
import SearchInput from "../molecules/Form/SearchInput";
import DropdownSubmenu from "./DropdownSubmenu";
const useStyles = () => {
    const { colors, space } = useTheme();
    const styles = StyleSheet.create({
        card: Object.assign({ shadowColor: colors.black, shadowOffset: {
                width: 4,
                height: 2,
            }, shadowOpacity: 0.05, shadowRadius: 4 }, Platform.select({
            android: {
                elevation: 2,
            },
        })),
        indicator: {
            backgroundColor: colors.gray[100],
            position: "absolute",
            right: space[1],
            width: space[1],
        },
    });
    return styles;
};
const Dropdown = (_a) => {
    var { list, title = "Title", hasUI = true, hasSearch = true, maxListHeight = 200, onReset, triggerClose } = _a, rest = __rest(_a, ["list", "title", "hasUI", "hasSearch", "maxListHeight", "onReset", "triggerClose"]);
    const { colors } = useTheme();
    const styles = useStyles();
    const [layout, setLayout] = useState({
        indicator: new Animated.Value(0),
        wholeHeight: 1,
        visibleHeight: 0,
    });
    const [search, setSearch] = useState("");
    const filteredList = useMemo(() => list.filter(({ text, subtext }) => `${text}${subtext !== null && subtext !== void 0 ? subtext : ""}`
        .toLowerCase()
        .includes(search.toLowerCase())), [list, search]);
    const [submenuVisible, setSubmenuVisible] = useState(false);
    const [submenuItems, setSubmenuItems] = useState([]);
    const handleResetPress = () => {
        setSearch("");
        if (onReset)
            onReset();
    };
    const handleSearchChange = (value) => {
        setSearch(value);
    };
    const { t } = useTranslation();
    const indicatorSize = layout.wholeHeight > layout.visibleHeight
        ? (layout.visibleHeight * layout.visibleHeight) / layout.wholeHeight
        : layout.visibleHeight;
    const diff = layout.visibleHeight > indicatorSize
        ? layout.visibleHeight - indicatorSize
        : 1;
    // used for mobile submenu
    const [subMenuPlace, setSubMenuPlace] = useState(0);
    const windowWidth = Dimensions.get("window").width;
    return (_jsx(Box, Object.assign({ bgColor: "white", borderRadius: "md", style: styles.card }, rest, { children: _jsxs(Stack, Object.assign({ direction: "column" }, { children: [title && hasUI && (_jsxs(Stack, Object.assign({ direction: "row", justifyContent: "space-between", "align-items": "baseline", p: 4, pb: 0 }, { children: [_jsx(Text, Object.assign({ fontWeight: "medium", display: "flex", alignItems: "center" }, { children: title })), hasSearch && (_jsx(Button, Object.assign({ onPress: handleResetPress, variant: "link", size: "xs", p: 0 }, { children: t("setback") })))] }))), hasSearch && hasUI && (_jsx(Box, Object.assign({ pt: 3, px: 3 }, { children: _jsx(SearchInput, { placeholder: t("search"), onChange: handleSearchChange, value: search }) }))), _jsxs(Box, Object.assign({ py: 3 }, { children: [_jsx(FlatList, { showsVerticalScrollIndicator: false, onContentSizeChange: (width, height) => {
                                setLayout((prevState) => (Object.assign(Object.assign({}, prevState), { wholeHeight: height })));
                            }, onLayout: ({ nativeEvent }) => setLayout((prevState) => (Object.assign(Object.assign({}, prevState), { visibleHeight: nativeEvent.layout.height }))), scrollEventThrottle: 16, onScroll: Animated.event([
                                {
                                    nativeEvent: {
                                        contentOffset: { y: layout.indicator },
                                    },
                                },
                            ], {
                                useNativeDriver: false,
                            }), maxHeight: maxListHeight, data: filteredList, renderItem: ({ item }) => {
                                const triggerPress = () => {
                                    if (item.onPress)
                                        item.onPress(item.key);
                                    if (triggerClose)
                                        triggerClose();
                                };
                                const openSubmenu = (e) => {
                                    if (item.submenu) {
                                        setSubmenuVisible(!submenuVisible);
                                        setSubmenuItems(item.submenu);
                                        setSubMenuPlace(e.nativeEvent.pageY);
                                    }
                                };
                                if (item.submenu) {
                                    return (_jsx(Popover, Object.assign({ placement: "left top", trigger: (triggerProps) => (_jsx(Pressable, Object.assign({ onPress: (e) => openSubmenu(e), _hover: {
                                                backgroundColor: "blueGray.50",
                                            }, _pressed: {
                                                backgroundColor: "blueGray.100",
                                            }, position: "relative" }, { children: _jsxs(Stack, Object.assign({ direction: "row", alignItems: "center", px: 3, py: 2.5 }, triggerProps, { children: [item.formControl && (_jsx(Box, Object.assign({ mr: 3 }, { children: item.formControl }))), item.avatar && item.avatar, item.iconName && (_jsx(Box, Object.assign({ mr: 2 }, { children: _jsx(Icon, { name: item.iconName, fill: colors
                                                                .gray[600] }) }))), _jsx(Text, Object.assign({ fontSize: "sm", color: "black", alignSelf: "flex-start", mr: item.subtext ? 1 : 0 }, { children: item.text })), item.subtext && (_jsx(Text, Object.assign({ fontSize: "sm", color: "gray.500", alignSelf: "flex-start" }, { children: item.subtext }))), _jsx(Box, Object.assign({ ml: 4 }, { children: _jsx(Icon, { name: "chevronRight", fill: colors.gray[600] }) }))] })) }))), isOpen: submenuVisible, onClose: () => setSubmenuVisible((prevState) => !prevState) }, { children: isWeb ? (_jsx(Popover.Content, Object.assign({ borderColor: "gray.100", bgColor: "transparent", overflow: "hidden" }, { children: _jsx(DropdownSubmenu, { maxWidth: "auto", list: submenuItems, triggerClose: triggerClose }) }))) : (_jsx(Box, Object.assign({ top: subMenuPlace + 20, right: -windowWidth / 2 + 35, width: windowWidth / 2 }, { children: _jsx(DropdownSubmenu, { list: submenuItems, triggerClose: triggerClose }) }))) })));
                                }
                                return (_jsx(Pressable, Object.assign({ onPress: triggerPress, px: 3, py: 2.5, _hover: {
                                        backgroundColor: "blueGray.50",
                                    }, _pressed: {
                                        backgroundColor: "blueGray.100",
                                    } }, { children: _jsxs(Stack, Object.assign({ direction: "row", alignItems: "center" }, { children: [item.formControl && (_jsx(Box, Object.assign({ mr: 3 }, { children: item.formControl }))), item.avatar && item.avatar, item.iconName && (_jsx(Box, Object.assign({ mr: 2 }, { children: _jsx(Icon, { name: item.iconName, fill: colors.gray[600] }) }))), _jsx(Text, Object.assign({ fontSize: "sm", color: "black", alignSelf: "flex-start", mr: item.subtext ? 1 : 0 }, { children: item.text })), _jsx(Text, Object.assign({ fontSize: "sm", color: "gray.500", alignSelf: "flex-start" }, { children: item.subtext }))] })) })));
                            }, keyExtractor: ({ key }, index) => key ? String(key) : index.toString() }), layout.wholeHeight > layout.visibleHeight && (_jsx(Animated.View, { style: [
                                styles.indicator,
                                {
                                    height: indicatorSize,
                                    transform: [
                                        {
                                            translateY: Animated.multiply(layout.indicator, layout.visibleHeight /
                                                layout.wholeHeight).interpolate({
                                                inputRange: [0, diff],
                                                outputRange: [0, diff],
                                                extrapolate: "clamp",
                                            }),
                                        },
                                    ],
                                },
                            ] }))] }))] })) })));
};
export default Dropdown;
